import { faDate } from '~/utils/faDate'
import { addTehranDays, tehranToday } from '~/utils/tehran'
import { groupSlotsByDaypart } from '~/utils/bookingDayparts'
import { intersectVisitTypes } from '~/utils/bookingVisitTypes'
import { formatFaCountdown, toFaDigits } from '~/utils/faDigits'
import { formatRialFa } from '~/utils/money'
import { formatIrMobileLocal, isCompleteIrMobile } from '~/utils/phone'
import { isValidNationalId, sanitizeNationalId } from '~/utils/nationalId'
import { apiErrorMessage } from '~/utils/apiError'
import type { BookingDoctorPublic, BookingServicePublic, BookingSlotPublic, BookingTicket, VisitTypes } from '~/types/booking'
import type { PatientMemberSummary } from '~/types/patient'
import type { PatientRelation } from '~/constants/patient/relations'
import { relationRequiredFromFetch } from '~/utils/patient/relationError'

export type BookingFlowStep = 'service' | 'quiz' | 'visit' | 'doctor' | 'slot' | 'otp' | 'confirm' | 'success'

export function useBookingFlow() {
  const tenant = useTenantSite()
  const businessType = ref(tenant.value?.businessType || '')
  const isClinic = computed(() => (tenant.value?.businessType || businessType.value) === 'clinic')

  const step = ref<BookingFlowStep>('service')
  /** The modal enters with a service already chosen, so it has no service step to return to. */
  const serviceStepEnabled = ref(true)
  const loading = ref(false)
  const error = ref('')
  const services = ref<BookingServicePublic[]>([])
  const doctors = ref<BookingDoctorPublic[]>([])
  const selectedService = ref<BookingServicePublic | null>(null)
  const selectedDoctor = ref<BookingDoctorPublic | null>(null)
  const visitType = ref<'in_person' | 'virtual'>('in_person')
  const slots = ref<BookingSlotPublic[]>([])
  const slotsLoading = ref(false)
  const selectedSlot = ref<BookingSlotPublic | null>(null)
  const altSlots = ref(new Set<string>())
  const phone = ref('')
  const phoneComplete = computed(() => isCompleteIrMobile(phone.value))
  const phoneLocal = computed(() => toFaDigits(formatIrMobileLocal(phone.value)))
  const otpSent = ref(false)
  const otpSending = ref(false)
  const otpVerifying = ref(false)
  const otpCode = ref<string[]>([])
  const otpCooldown = ref(0)
  const otpCooldownLabel = computed(() => formatFaCountdown(otpCooldown.value))
  let cooldownTimer: ReturnType<typeof setInterval> | null = null
  const bookingToken = ref('')
  const holdToken = ref('')
  const firstName = ref('')
  const lastName = ref('')
  const nationalId = ref('')
  const nationalIdValid = computed(() => isValidNationalId(nationalId.value))
  const nationalIdError = computed(() => {
    const value = nationalId.value
    if (!value) return ''
    if (value.length < 10) return `کد ملی باید ۱۰ رقم باشد (${toFaDigits(String(value.length))} رقم)`
    return nationalIdValid.value ? '' : 'کد ملی معتبر نیست'
  })
  const identityComplete = computed(() => (
    firstName.value.trim().length >= 2
    && lastName.value.trim().length >= 2
    && nationalIdValid.value
    && (!existingMembers.value.length || Boolean(relation.value))
  ))
  const relation = ref<PatientRelation | undefined>(undefined)
  const existingMembers = ref<PatientMemberSummary[]>([])
  const note = ref('')
  const couponCode = ref('')
  const giftCardCode = ref('')
  const couponDiscount = ref(0)
  const couponPreviewError = ref('')
  const couponApplying = ref(false)
  const payMethod = ref<'free' | 'onsite' | 'gateway'>('free')
  const paymentGateways = ref<Array<{ provider: string; label: string }>>([])
  const paymentGateway = ref('')
  const paymentGatewayLoadFailed = ref(false)
  const campaignEnabled = computed(() =>
    (tenant.value?.modules ?? []).some((module) => module.key === 'clinicCampaign' && module.enabled),
  )
  const submitting = ref(false)
  const ticket = ref<BookingTicket | null>(null)
  /** Alias kept so existing templates that read `success` keep working. */
  const success = ticket

  const minDate = tehranToday()
  const maxDate = addTehranDays(minDate, 30)
  const selectedDay = ref(minDate)
  const availableDates = ref<string[]>([])
  const availableSlotCounts = ref<Record<string, number>>({})
  const visibleRange = ref<{ from: string; to: string } | null>(null)
  const selectedDayLabel = computed(() => faDate(`${selectedDay.value}T12:00:00+03:30`))

  /** What the chosen slot actually offers, narrowed by the service's own types. */
  const slotVisitTypes = computed<VisitTypes>(() => {
    const service = selectedService.value?.visitTypes ?? 'both'
    const slot = selectedSlot.value?.visitTypes ?? 'both'
    return intersectVisitTypes(service, slot)
  })

  const needsVisitChoice = computed(() => slotVisitTypes.value === 'both')

  /**
   * Slots are fetched before the visit type is known, so only a service that is
   * limited to one type narrows the query; `both` must return every slot so the
   * next step can offer the real choice.
   */
  const slotQueryVisitType = computed(() => {
    const types = selectedService.value?.visitTypes
    return types === 'in_person' || types === 'virtual' ? types : undefined
  })

  const allowsInPerson = computed(() => slotVisitTypes.value !== 'virtual')
  const allowsVirtual = computed(() => slotVisitTypes.value !== 'in_person')

  // The visit type is asked *after* the time, because whether a slot supports
  // in-person, virtual, or both depends on the schedule window it came from.
  const quizPassed = ref(false)
  const quizFailed = ref(false)
  const quizAnswers = ref<Record<string, unknown>>({})
  const quizPhone = ref('')
  const quizQuestions = ref<Array<{ id: string; question: string; type: string; options?: Array<{ value: string; label: string }>; required?: boolean }>>([])
  const quizSubmitting = ref(false)
  const quizResult = ref<{ title?: string; description?: string } | null>(null)

  const stepLabels = computed(() => {
    const items = [{ id: 'service', label: 'خدمت' }]
    if (selectedService.value?.readinessQuizId) items.push({ id: 'quiz', label: 'آمادگی' })
    if (isClinic.value) items.push({ id: 'doctor', label: 'پزشک' })
    items.push({ id: 'slot', label: 'زمان' })
    // Before a slot exists we can only guess from the service; once one is picked
    // the slot itself decides whether a visit-type choice actually appears.
    const visitStep = selectedSlot.value
      ? needsVisitChoice.value
      : selectedService.value?.visitTypes === 'both'
    if (visitStep) items.push({ id: 'visit', label: 'نوع نوبت' })
    items.push({ id: 'otp', label: 'تأیید' }, { id: 'confirm', label: 'ثبت' })
    return items
  })

  const modalStepLabels = computed(() => stepLabels.value.filter((item) => item.id !== 'service'))

  /** The steps actually shown as progress in the current entry point (modal vs. full page). */
  const activeStepLabels = computed(() =>
    serviceStepEnabled.value ? stepLabels.value : modalStepLabels.value,
  )

  const stepIndex = computed(() =>
    activeStepLabels.value.findIndex((item) => item.id === step.value),
  )
  const stepTotal = computed(() => activeStepLabels.value.length)
  const stepNumber = computed(() => (stepIndex.value < 0 ? stepTotal.value : stepIndex.value + 1))
  const progressPercent = computed(() => {
    if (step.value === 'success') return 100
    if (!stepTotal.value) return 0
    return Math.round((stepNumber.value / stepTotal.value) * 100)
  })

  const stepTitle = computed(() => {
    switch (step.value) {
      case 'service': return 'خدمت مورد نظر را انتخاب کنید'
      case 'quiz': return quizFailed.value ? 'این خدمت الان مناسب نیست' : 'چند سؤال کوتاه قبل از نوبت'
      case 'visit': return 'این نوبت را چطور می‌خواهید؟'
      case 'doctor': return 'پزشک خود را انتخاب کنید'
      case 'slot': return 'روز و ساعت را انتخاب کنید'
      case 'otp': return otpSent.value ? 'کد تأیید را وارد کنید' : 'شماره موبایل خود را وارد کنید'
      case 'confirm': return 'اطلاعات نهایی و ثبت نوبت'
      default: return ''
    }
  })

  const stepHint = computed(() => {
    switch (step.value) {
      case 'visit': return selectedSlotLabel.value
        ? `برای ${selectedSlotLabel.value} هر دو حالت ممکن است.`
        : 'ویزیت حضوری در مطب یا ویزیت آنلاین از راه دور.'
      case 'doctor': return 'ساعت‌های خالی بر اساس برنامهٔ همان پزشک نمایش داده می‌شود.'
      case 'slot': return 'روزهای دارای ظرفیت با نقطه مشخص شده‌اند.'
      case 'otp': return otpSent.value
        // Bidi isolate keeps the grouped number from being reordered inside the RTL sentence.
        ? `کد ۶ رقمی به شمارهٔ \u2066${phoneLocal.value}\u2069 پیامک شد.`
        : 'برای تأیید هویت و ارسال یادآور، یک کد پیامک می‌کنیم.'
      case 'confirm': return 'پیش از ثبت، خلاصهٔ نوبت را بررسی کنید.'
      default: return ''
    }
  })

  const paymentOptions = computed(() => {
    const service = selectedService.value
    if (!service) return []
    if (service.paymentMode === 'free') {
      return [{ label: 'بدون پرداخت', description: 'این خدمت رایگان است.', value: 'free' }]
    }
    const amount = service.paymentMode === 'deposit'
      ? (service.depositAmount || 0)
      : service.price
    const items = [{
      label: service.paymentMode === 'deposit' ? 'پرداخت بیعانه' : 'پرداخت آنلاین',
      description: `${formatRialFa(amount)} — انتقال به درگاه بانکی`,
      value: 'gateway',
    }]
    if (service.paymentMode === 'full' && service.allowOnsitePayment && visitType.value === 'in_person') {
      items.push({
        label: 'پرداخت حضوری در مطب',
        description: `${formatRialFa(service.price)} هنگام مراجعه`,
        value: 'onsite',
      })
    }
    return items
  })

  /** Jalali day label such as «شنبه ۱۵ مرداد». */
  function faDayLabel(dateStr: string, withWeekday = true) {
    const date = new Date(`${dateStr}T12:00:00+03:30`)
    if (Number.isNaN(date.getTime())) return dateStr
    try {
      return toFaDigits(date.toLocaleDateString('fa-IR', {
        ...(withWeekday ? { weekday: 'long' } : {}),
        day: 'numeric',
        month: 'long',
      }))
    }
    catch {
      return dateStr
    }
  }

  /** Quick-pick strip for the first days of the booking horizon. */
  const dayChips = computed(() => {
    const items: { date: string; weekday: string; day: string; isToday: boolean }[] = []
    for (let offset = 0; offset < 14; offset += 1) {
      const date = addTehranDays(minDate, offset)
      if (date > maxDate) break
      const parsed = new Date(`${date}T12:00:00+03:30`)
      items.push({
        date,
        weekday: offset === 0
          ? 'امروز'
          : offset === 1
            ? 'فردا'
            : toFaDigits(parsed.toLocaleDateString('fa-IR', { weekday: 'short' })),
        day: toFaDigits(parsed.toLocaleDateString('fa-IR', { day: 'numeric', month: 'short' })),
        isToday: offset === 0,
      })
    }
    return items
  })

  const slotGroups = computed(() => groupSlotsByDaypart(slots.value))

  /** Where the “back” control should land, or `null` when this is the first step. */
  const previousStep = computed<BookingFlowStep | null>(() => {
    switch (step.value) {
      case 'quiz':
        return serviceStepEnabled.value ? 'service' : null
      case 'doctor':
        if (selectedService.value?.readinessQuizId) return 'quiz'
        return serviceStepEnabled.value ? 'service' : null
      case 'slot':
        if (isClinic.value) return 'doctor'
        if (selectedService.value?.readinessQuizId) return 'quiz'
        return serviceStepEnabled.value ? 'service' : null
      case 'visit':
        return 'slot'
      case 'otp':
        return needsVisitChoice.value ? 'visit' : 'slot'
      // The phone is already verified here, so returning keeps the token valid.
      case 'confirm':
        return needsVisitChoice.value ? 'visit' : 'slot'
      default:
        return null
    }
  })

  const canGoBack = computed(() => previousStep.value !== null && !submitting.value)

  /** Whether the step navigation bar has anything to show. */
  const showStepNav = computed(() => {
    if (step.value === 'service') return false
    // The success step only needs the bar when there is a virtual visit link to offer.
    if (step.value === 'success') return ticket.value?.visitType === 'virtual'
    return canGoBack.value || step.value === 'quiz' || step.value === 'slot' || step.value === 'otp' || step.value === 'confirm'
  })

  const submitLabel = computed(() => (
    payMethod.value === 'gateway' ? 'پرداخت و ثبت نوبت' : 'ثبت نوبت'
  ))

  const selectedSlotLabel = computed(() => {
    const slot = selectedSlot.value
    if (!slot) return ''
    return `${faDayLabel(slot.date)} ساعت ${toFaDigits(slot.time)}`
  })

  function goBack() {
    const target = previousStep.value
    if (!target) return
    error.value = ''
    if (target === 'service') {
      selectedService.value = null
      selectedDoctor.value = null
    }
    if (target === 'slot') altSlots.value = new Set()
    step.value = target
  }

  const summaryRows = computed(() => {
    const rows: { label: string; value: string; icon: string; ltr?: boolean }[] = []
    if (selectedService.value) {
      rows.push({ label: 'خدمت', value: selectedService.value.title, icon: 'i-lucide-stethoscope' })
    }
    if (selectedDoctor.value?.name) {
      rows.push({ label: 'پزشک', value: selectedDoctor.value.name, icon: 'i-lucide-user-round' })
    }
    rows.push({
      label: 'نوع نوبت',
      value: visitType.value === 'virtual' ? 'ویزیت مجازی' : 'ویزیت حضوری',
      icon: visitType.value === 'virtual' ? 'i-lucide-video' : 'i-lucide-map-pin',
    })
    if (selectedSlot.value) {
      rows.push({
        label: 'زمان',
        value: `${faDayLabel(selectedSlot.value.date)} · ساعت ${toFaDigits(selectedSlot.value.time)}`,
        icon: 'i-lucide-calendar-clock',
      })
      const slotMinutes = Math.round(
        (new Date(selectedSlot.value.endAtUtc).getTime() - new Date(selectedSlot.value.startAtUtc).getTime()) / 60_000,
      )
      if (slotMinutes > 0) {
        rows.push({
          label: 'مدت ویزیت',
          value: `${toFaDigits(slotMinutes)} دقیقه`,
          icon: 'i-lucide-timer',
        })
      }
    } else if (selectedService.value?.durationMin) {
      rows.push({
        label: 'مدت ویزیت',
        value: `${toFaDigits(selectedService.value.durationMin)} دقیقه`,
        icon: 'i-lucide-timer',
      })
    }
    if (phoneComplete.value) {
      rows.push({ label: 'موبایل', value: phoneLocal.value, icon: 'i-lucide-phone', ltr: true })
    }
    return rows
  })

  const payableAmount = computed(() => {
    const service = selectedService.value
    if (!service || service.paymentMode === 'free') return 0
    return service.paymentMode === 'deposit'
      ? (service.depositAmount || 0)
      : service.price
  })

  /** The amount the patient pays now, given the chosen payment method. */
  const amountDueLabel = computed(() => {
    const service = selectedService.value
    if (!service || service.paymentMode === 'free') return 'رایگان'
    if (payMethod.value === 'onsite') return 'پرداخت در مطب'
    const amount = Math.max(0, payableAmount.value - couponDiscount.value)
    return formatRialFa(amount)
  })

  async function previewCoupon() {
    couponPreviewError.value = ''
    couponDiscount.value = 0
    const code = couponCode.value.trim()
    if (!code || !selectedService.value || payableAmount.value <= 0) return
    couponApplying.value = true
    try {
      const result = await $fetch<{ item: { discount: number } }>('/api/campaign/coupons/apply', {
        method: 'POST',
        body: {
          code,
          serviceId: selectedService.value.id,
          amount: payableAmount.value,
          claim: false,
        },
      })
      couponDiscount.value = result.item.discount
    }
    catch (err) {
      couponPreviewError.value = apiErrorMessage(err, 'کد تخفیف معتبر نیست')
    }
    finally {
      couponApplying.value = false
    }
  }

  function visitTypesLabel(value: string) {
    if (value === 'virtual') return 'مجازی'
    if (value === 'both') return 'حضوری و مجازی'
    return 'حضوری'
  }

  function paymentLabel(service: BookingServicePublic) {
    if (service.paymentMode === 'free') return 'رایگان'
    if (service.paymentMode === 'deposit') return 'بیعانه'
    return 'پرداخت کامل'
  }

  async function loadServices() {
    loading.value = true
    error.value = ''
    try {
      const data = await $fetch<{ items: BookingServicePublic[]; businessType?: string }>('/api/booking/services')
      services.value = data.items
      if (data.businessType) businessType.value = data.businessType
      paymentGatewayLoadFailed.value = false
      try {
        const gateways = await $fetch<{ items: Array<{ provider: string; label: string }> }>('/api/payments/gateways')
        paymentGateways.value = gateways.items
        paymentGateway.value = gateways.items[0]?.provider || ''
      }
      catch (err) {
        paymentGateways.value = []
        paymentGateway.value = ''
        paymentGatewayLoadFailed.value = true
        error.value = apiErrorMessage(err, 'بارگذاری درگاه پرداخت ناموفق بود')
      }
    }
    catch (err) {
      error.value = apiErrorMessage(err, 'بارگذاری خدمات ناموفق بود')
    }
    finally {
      loading.value = false
    }
  }

  /** Cards on other pages may carry a different id for the same service. */
  function findServiceByRef(ref: string) {
    return services.value.find((item) =>
      item.serviceRef === ref
      || item.id === ref
      || (item.refs ?? []).includes(ref),
    )
  }

  function applyPreselect(ref: string) {
    if (!ref || step.value !== 'service') return
    const match = findServiceByRef(ref)
    if (!match) return
    chooseService(match)
  }

  function chooseService(service: BookingServicePublic) {
    selectedService.value = service
    quizPassed.value = false
    quizFailed.value = false
    quizAnswers.value = {}
    quizResult.value = null
    if (service.visitTypes !== 'both') {
      visitType.value = service.visitTypes === 'virtual' ? 'virtual' : 'in_person'
    }
    afterService()
  }

  async function submitReadinessQuiz() {
    const quizId = selectedService.value?.readinessQuizId
    if (!quizId) return
    quizSubmitting.value = true
    error.value = ''
    try {
      const res = await $fetch<{ item: { passed: boolean; result: { title?: string; description?: string } | null } }>(`/api/leads/quizzes/${quizId}/submit`, {
        method: 'POST',
        body: { answers: quizAnswers.value, phone: quizPhone.value || undefined },
      })
      quizResult.value = res.item.result
      if (res.item.passed) {
        quizPassed.value = true
        quizFailed.value = false
        await afterService()
      }
      else {
        quizFailed.value = true
      }
    }
    catch (err) {
      error.value = apiErrorMessage(err, 'ارسال آزمون ناموفق بود')
    }
    finally {
      quizSubmitting.value = false
    }
  }

  function chooseVisit(type: 'in_person' | 'virtual') {
    visitType.value = type
    step.value = bookingToken.value ? 'confirm' : 'otp'
  }

  async function afterService() {
    if (selectedService.value?.readinessQuizId && !quizPassed.value) {
      quizFailed.value = false
      quizAnswers.value = {}
      quizResult.value = null
      step.value = 'quiz'
      try {
        const data = await $fetch<{ item: { questions: typeof quizQuestions.value } }>(`/api/leads/quizzes/${selectedService.value.readinessQuizId}`)
        quizQuestions.value = data.item.questions ?? []
      }
      catch (err) {
        error.value = apiErrorMessage(err, 'بارگذاری آزمون ناموفق بود')
      }
      return
    }
    if (isClinic.value) {
      loading.value = true
      try {
        const data = await $fetch<{ items: BookingDoctorPublic[] }>('/api/booking/doctors', {
          query: { serviceId: selectedService.value?.id },
        })
        doctors.value = data.items
        step.value = 'doctor'
      }
      catch (err) {
        error.value = apiErrorMessage(err, 'بارگذاری پزشکان ناموفق بود')
      }
      finally {
        loading.value = false
      }
      return
    }
    step.value = 'slot'
    await loadSlots()
  }

  function chooseDoctor(doctor: BookingDoctorPublic) {
    selectedDoctor.value = doctor
    step.value = 'slot'
    loadSlots()
  }

  function selectSlot(slot: BookingSlotPublic) {
    selectedSlot.value = slot
  }

  async function loadSlots() {
    if (!selectedService.value) return
    slotsLoading.value = true
    selectedSlot.value = null
    try {
      const data = await $fetch<{ items: BookingSlotPublic[] }>('/api/booking/slots', {
        query: {
          serviceId: selectedService.value.id,
          doctorId: selectedDoctor.value?.id,
          from: selectedDay.value,
          to: selectedDay.value,
          visitType: slotQueryVisitType.value,
        },
      })
      slots.value = data.items
      availableSlotCounts.value = {
        ...availableSlotCounts.value,
        [selectedDay.value]: data.items.length,
      }
    }
    catch (err) {
      error.value = apiErrorMessage(err, 'بارگذاری نوبت‌ها ناموفق بود')
      slots.value = []
    }
    finally {
      slotsLoading.value = false
    }
  }

  watch(selectedDay, () => {
    if (step.value === 'slot') loadSlots()
  })

  watch(step, (value) => {
    if (value === 'slot') loadMonthAvailability()
  })

  onScopeDispose(stopCooldown)

  function onMonthChange(range: { from: string; to: string }) {
    visibleRange.value = range
    loadMonthAvailability()
  }

  function applySlotAvailability(items: BookingSlotPublic[], from: string, to: string) {
    const next = { ...availableSlotCounts.value }
    for (let date = from; date <= to; date = addTehranDays(date, 1)) {
      next[date] = 0
    }
    for (const slot of items) {
      next[slot.date] = (next[slot.date] ?? 0) + 1
    }
    availableSlotCounts.value = next
    availableDates.value = Object.entries(next)
      .filter(([, count]) => count > 0)
      .map(([date]) => date)
      .sort()
  }

  /** Highlights days with free capacity and counts slots per day (chips + visible month). */
  async function loadMonthAvailability() {
    if (!selectedService.value) {
      availableDates.value = []
      availableSlotCounts.value = {}
      return
    }
    const chipEnd = addTehranDays(minDate, 13)
    const horizonEnd = chipEnd > maxDate ? maxDate : chipEnd
    let from = minDate
    let to = horizonEnd
    const range = visibleRange.value
    if (range) {
      const rangeTo = range.to > maxDate ? maxDate : range.to
      if (rangeTo > to) to = rangeTo
    }
    try {
      const data = await $fetch<{ items: BookingSlotPublic[] }>('/api/booking/slots', {
        query: {
          serviceId: selectedService.value.id,
          doctorId: selectedDoctor.value?.id,
          from,
          to,
          visitType: slotQueryVisitType.value,
        },
      })
      applySlotAvailability(data.items, from, to)
    }
    catch {
      applySlotAvailability([], from, to)
    }
  }

  function goPhone() {
    if (!selectedSlot.value) return
    if (needsVisitChoice.value) {
      step.value = 'visit'
      return
    }
    visitType.value = slotVisitTypes.value === 'virtual' ? 'virtual' : 'in_person'
    // Coming back to change the time must not force a second verification.
    step.value = bookingToken.value ? 'confirm' : 'otp'
  }

  function onNationalIdInput(value: string) {
    nationalId.value = sanitizeNationalId(value)
  }

  function stopCooldown() {
    if (cooldownTimer) clearInterval(cooldownTimer)
    cooldownTimer = null
  }

  /** The server allows one code per phone per minute, so mirror that as a countdown. */
  function startCooldown(seconds = 60) {
    stopCooldown()
    otpCooldown.value = seconds
    cooldownTimer = setInterval(() => {
      otpCooldown.value -= 1
      if (otpCooldown.value <= 0) stopCooldown()
    }, 1000)
  }

  async function sendOtp() {
    if (otpSending.value || otpCooldown.value > 0) return
    otpSending.value = true
    error.value = ''
    try {
      await $fetch('/api/booking/otp/send', { method: 'POST', body: { phone: phone.value } })
      otpSent.value = true
      otpCode.value = []
      startCooldown()
    }
    catch (err) {
      error.value = apiErrorMessage(err, 'ارسال کد ناموفق بود')
    }
    finally {
      otpSending.value = false
    }
  }

  /** Lets the patient correct a mistyped number without losing the chosen slot. */
  function editPhone() {
    stopCooldown()
    otpSent.value = false
    otpCooldown.value = 0
    otpCode.value = []
    bookingToken.value = ''
    error.value = ''
    step.value = 'otp'
  }

  async function verifyOtp() {
    if (otpVerifying.value || otpCode.value.join('').length !== 6) return
    otpVerifying.value = true
    error.value = ''
    try {
      const data = await $fetch<{ bookingToken: string }>('/api/booking/otp/verify', {
        method: 'POST',
        body: { phone: phone.value, code: otpCode.value.join('') },
      })
      bookingToken.value = data.bookingToken
      stopCooldown()
      otpCooldown.value = 0
      if (selectedService.value?.paymentMode === 'free') payMethod.value = 'free'
      else payMethod.value = 'gateway'
      step.value = 'confirm'
    }
    catch (err) {
      error.value = apiErrorMessage(err, 'کد نادرست است')
      otpCode.value = []
    }
    finally {
      otpVerifying.value = false
    }
  }

  async function submitBooking() {
    if (!selectedService.value || !selectedSlot.value) return
    if (
      selectedService.value.paymentMode !== 'free'
      && payMethod.value === 'gateway'
      && paymentGatewayLoadFailed.value
    ) {
      error.value = 'امکان بررسی تنظیمات درگاه پرداخت نیست؛ صفحه را تازه‌سازی کنید و دوباره تلاش کنید.'
      return
    }
    submitting.value = true
    error.value = ''
    try {
      const hold = await $fetch<{ holdToken: string }>('/api/booking/hold', {
        method: 'POST',
        body: {
          serviceId: selectedService.value.id,
          doctorId: selectedDoctor.value?.id,
          startAt: selectedSlot.value.startAtUtc,
          phone: phone.value,
        },
      })
      holdToken.value = hold.holdToken
      const result = await $fetch<{
        paymentUrl: string | null
        appointment?: BookingTicket
      }>('/api/booking/appointments', {
        method: 'POST',
        body: {
          holdToken: hold.holdToken,
          bookingToken: bookingToken.value,
          serviceId: selectedService.value.id,
          doctorId: selectedDoctor.value?.id,
          startAt: selectedSlot.value.startAtUtc,
          visitType: visitType.value,
          firstName: firstName.value.trim(),
          lastName: lastName.value.trim(),
          nationalId: nationalId.value,
          relation: relation.value,
          payAtClinic: payMethod.value === 'onsite',
          note: note.value.trim() || undefined,
          couponCode: couponCode.value.trim() || undefined,
          giftCardCode: giftCardCode.value.trim() || undefined,
          provider: payMethod.value === 'gateway' ? (paymentGateway.value || undefined) : undefined,
        },
      })
      if (result.paymentUrl) {
        await navigateTo(result.paymentUrl, { external: true })
        return
      }
      if (!result.appointment) {
        error.value = 'نوبت ثبت شد ولی تیکت دریافت نشد'
        return
      }
      ticket.value = result.appointment
      step.value = 'success'
    }
    catch (err) {
      const required = relationRequiredFromFetch(err)
      if (required) {
        existingMembers.value = required.members
        error.value = required.members[0]
          ? `یک حساب با این شماره به نام ${required.members[0].fullName} وجود دارد`
          : apiErrorMessage(err, 'نسبت این فرد را مشخص کنید')
        return
      }
      const status = (err as { statusCode?: number })?.statusCode
      error.value = apiErrorMessage(err, 'ثبت نوبت ناموفق بود')
      if (status === 409) {
        await loadSlots()
        altSlots.value = new Set(slots.value.slice(0, 4).map((slot) => slot.startAtUtc))
        step.value = 'slot'
      }
    }
    finally {
      submitting.value = false
    }
  }

  async function startWithService(service: BookingServicePublic | string) {
    serviceStepEnabled.value = false
    if (typeof service === 'string') {
      if (!services.value.length || paymentGatewayLoadFailed.value) await loadServices()
      const match = findServiceByRef(service)
      if (!match) {
        error.value = 'این خدمت قابل رزرو نیست'
        return
      }
      chooseService(match)
      return
    }
    if (!services.value.some((item) => item.id === service.id)) {
      services.value = [service, ...services.value]
    }
    serviceStepEnabled.value = false
    chooseService(service)
  }

  /** Return from the payment gateway onto the success ticket. */
  async function resumeFromPayment(appointmentId: string) {
    loading.value = true
    error.value = ''
    try {
      const data = await $fetch<BookingTicket>(`/api/booking/appointments/${appointmentId}/ticket`)
      ticket.value = data
      step.value = 'success'
    }
    catch (err) {
      error.value = apiErrorMessage(err, 'نوبت یافت نشد')
      step.value = 'service'
    }
    finally {
      loading.value = false
    }
  }

  function markPaymentFailed() {
    ticket.value = null
    step.value = 'service'
    error.value = 'پرداخت ناموفق بود. اگر مبلغ از حسابتان کسر شده، با مطب تماس بگیرید.'
  }

  function reset() {
    step.value = 'service'
    serviceStepEnabled.value = true
    loading.value = false
    error.value = ''
    selectedService.value = null
    selectedDoctor.value = null
    visitType.value = 'in_person'
    slots.value = []
    selectedSlot.value = null
    altSlots.value = new Set()
    phone.value = ''
    otpSent.value = false
    otpSending.value = false
    otpVerifying.value = false
    otpCode.value = []
    stopCooldown()
    otpCooldown.value = 0
    bookingToken.value = ''
    holdToken.value = ''
    firstName.value = ''
    lastName.value = ''
    nationalId.value = ''
    relation.value = undefined
    existingMembers.value = []
    note.value = ''
    couponCode.value = ''
    giftCardCode.value = ''
    couponDiscount.value = 0
    couponPreviewError.value = ''
    payMethod.value = 'free'
    submitting.value = false
    ticket.value = null
    selectedDay.value = minDate
    availableDates.value = []
    availableSlotCounts.value = {}
    visibleRange.value = null
  }

  return reactive({
    isClinic,
    step,
    loading,
    error,
    services,
    doctors,
    selectedService,
    selectedDoctor,
    visitType,
    slots,
    slotsLoading,
    selectedSlot,
    altSlots,
    phone,
    phoneComplete,
    otpSent,
    otpSending,
    otpVerifying,
    otpCode,
    otpCooldown,
    otpCooldownLabel,
    phoneLocal,
    firstName,
    lastName,
    nationalId,
    relation,
    existingMembers,
    nationalIdValid,
    nationalIdError,
    identityComplete,
    onNationalIdInput,
    note,
    payMethod,
    paymentGateways,
    paymentGateway,
    paymentGatewayLoadFailed,
    submitting,
    ticket,
    success,
    minDate,
    maxDate,
    selectedDay,
    availableDates,
    availableSlotCounts,
    selectedDayLabel,
    allowsInPerson,
    allowsVirtual,
    slotVisitTypes,
    needsVisitChoice,
    selectedSlotLabel,
    stepLabels,
    modalStepLabels,
    activeStepLabels,
    stepIndex,
    stepNumber,
    stepTotal,
    progressPercent,
    stepTitle,
    stepHint,
    dayChips,
    slotGroups,
    previousStep,
    canGoBack,
    goBack,
    showStepNav,
    submitLabel,
    summaryRows,
    amountDueLabel,
    payableAmount,
    campaignEnabled,
    couponCode,
    giftCardCode,
    couponDiscount,
    couponPreviewError,
    couponApplying,
    previewCoupon,
    faDayLabel,
    editPhone,
    paymentOptions,
    visitTypesLabel,
    paymentLabel,
    loadServices,
    applyPreselect,
    quizPassed,
    quizFailed,
    quizAnswers,
    quizPhone,
    quizQuestions,
    quizSubmitting,
    quizResult,
    submitReadinessQuiz,
    chooseService,
    chooseVisit,
    chooseDoctor,
    selectSlot,
    onMonthChange,
    goPhone,
    sendOtp,
    verifyOtp,
    submitBooking,
    startWithService,
    resumeFromPayment,
    markPaymentFailed,
    reset,
    toFaDigits,
    faDate,
  })
}
